/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { scaleLinear } from 'd3-scale'
import ScrollyStory from 'components/ScrollyStory'
import { Mdx, Link } from '@ta-interaktiv/contentbricks'
import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'
import browsercheck from '@ta-interaktiv/browsercheck'
import { MDXProvider } from '@mdx-js/react'

// TODO Set the correct article ID here
const ARTICLE_ID = '360910906775'

const YEARS = {
  2003: 22,
  2004: 5,
  2005: 3,
  2006: 11,
  2007: 8,
  2008: 2,
  2009: 3,
  2010: 4,
  2011: 19,
  2012: 8,
  2013: 4,
  2014: 20,
  2015: 16,
  2016: 11,
  2017: 15,
  2018: 28,
  2019: 15
}

const COLOR_SCALE = [
  'rgb(210,62,117)',
  'rgb(211,98,137)',
  'rgb(212,135,156)',
  'rgb(213,172,176)',
  'rgb(214,209,195)',
  'rgb(180,200,188)',
  'rgb(146,190,181)',
  'rgb(112,181,174)',
  'rgb(78,172,166)',
  'rgb(44,162,159)'
]

const scrollScale = scaleLinear()
  .domain([0, 1])
  .range([2003, 2019])

const Legend = () => (
  <div className='map-legend-colors'>
    <div className='map-legend-colors-title'>Vitalität</div>
    <div className='map-legend-colors-swatches'>
      {COLOR_SCALE.map(c => (
        <div key={c} className='map-legend-swatch' style={{ background: c }} />
      ))}
    </div>
    <div className='map-legend-colors-label'>
      <div>Niedrig</div>
      <div>Hoch</div>
    </div>
  </div>
)

export function AppComponent() {
  return (
    <MDXProvider
      components={{
        a: Link,
        blockquote: props => <div className='quote-element' {...props} />
      }}
    >
      <div className='index'>
        <Masthead articleId={ARTICLE_ID} />
        <ThemeSwitch />

        <header className='ui vertical very fitted segment'>
          <div className='ui container'>
            <div className='ui header'>
              <Mdx
                className='ta text'
                file={require('text/DcN68UY7dOrL.mdx')}
              />
            </div>
          </div>
        </header>

        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='ta metainfo'>
              <div className='ta author'>Yannick Wiget, Andreas Moor</div>
            </div>

            <Mdx className='ta text' file={require('text/Pyr2D4VRddRv.mdx')} />
          </div>
        </div>
        <ScrollyStory
          length={2}
          render={(width, height, progress, direction) => {
            const active = Math.round(scrollScale(progress))
            const imageContainer = React.useRef(null)

            const mapW = Math.min((1111 * height) / 900, 1111)
            const mapH = Math.min((775 * height) / 900, 775)

            React.useEffect(() => {
              imageContainer.current.scrollTo(Math.abs(width - mapW) / 2, 0)
              // .scroll(200)
            }, [imageContainer.current])

            return (
              <div className='map-container'>
                <div
                  className='map-header'
                  style={{ position: 'absolute', top: 0, width: '100%' }}
                >
                  <div className='ui text container chart'>
                    <div
                      className='map-legend'
                      style={{ position: 'relative', zIndex: 10 }}
                    >
                      <div className='map-legend-description'>
                        <div className='map-legend-title'>{active}</div>
                        <div>
                          Die Messung des Farbspektrums des von den Blättern
                          reflektierten Lichts ergibt, dass {active}{' '}
                          <strong>{YEARS[active]} Prozent</strong> der Schweizer
                          Bäume
                          <br />
                          gestresst waren.
                        </div>
                      </div>
                      <Legend />
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    width,
                    height,
                    overflowX: 'auto',
                    overflowY: 'hidden'
                  }}
                  x
                  ref={imageContainer}
                >
                  <div
                    style={{
                      height: mapH,
                      width: mapW,
                      margin: 'auto',
                      marginTop: (height - mapH) / 2,
                      position: 'relative'
                    }}
                  >
                    {Object.keys(YEARS).map(y => (
                      <img
                        key={y}
                        src={require(`images/maps/${y}_1111x775.png`)}
                        style={{
                          height: mapH,
                          width: mapW,
                          position: 'absolute',
                          top: '2%',
                          left: 0,
                          opacity: `${active}` === y ? 1 : 0
                        }}
                      />
                    ))}
                  </div>
                  <div
                    className='map-footer'
                    style={{ position: 'absolute', bottom: 0, width: '100%' }}
                  >
                    <div
                      className='ui text container chart'
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}
                    >
                      <div
                        style={{
                          width: mapW / 7,
                          borderTop: '1px solid #202444',
                          textAlign: 'center',
                          fontSize: '0.65rem'
                        }}
                      >
                        50 Kilomenter
                      </div>
                      <div style={{ fontSize: '0.65rem' }}>
                        Quelle: Waldzustandsmonitor
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          }}
        />

        <div className='ui vertical segment'>
          <div className='ui text container'>
            <Mdx className='ta text' file={require('text/jTArqHpAdv0J.mdx')} />
            <div style={{ margin: '2rem auto' }}>
              <AutofitIFrame
                aria-label='Interactive bar chart'
                src='//datawrapper.dwcdn.net/daESR/4/'
              />
            </div>
            <Mdx className='ta text' file={require('text/INKqGcwBd6fl.mdx')} />
          </div>
        </div>

        <div className='ui vertical segment'>
          <div
            className='ui container'
            style={{
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                maxWidth: 3 * 335
              }}
            >
              <div className='map-legend-description' style={{ width: 335 }}>
                <div className='map-legend-title'>2003–2019</div>
                <div>
                  Vitalität des Baumbestandes gemessen an den Rot-Grün-Anteilen
                  der Färbung der Blätter.
                </div>
                <br />
                <div className='ui mobile hidden' style={{ width: 335 }}>
                  <Legend />
                </div>
              </div>
              <div className='ui mobile only' style={{ width: 165 }}>
                <Legend />
              </div>
              {Object.keys(YEARS).map(j => (
                <div key={j} style={{ position: 'relative' }}>
                  <div className='minimap-label'>{j}</div>
                  <img
                    className='minimap'
                    src={require(`images/maps/${j}_335x235.png`)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className='ui vertical segment'>
          <div className='ui text container'>
            <Mdx className='ta text' file={require('text/Toww8fdpKH82.mdx')} />
            <div className='image'>
              <img src={require(`images/borkenkaefer.jpg`)} width='100%' />
              <div className='caption'>
                Fallen über geschwächte Bäume her: Borkenkäfer fressen Löcher in
                die Rinde und den Bast einer Fichte.{' '}
                <span className='credits'>Foto: Keystone</span>
              </div>
            </div>
            <Mdx className='ta text' file={require('text/amvRGvAxelBB.mdx')} />
          </div>
        </div>

        <div className='ui vertical segment'>
          <div className='ui text container' />
        </div>

        <Footer
          questions={[
            'Wie informativ war der Beitrag?',
            'Wie gefällt Ihnen die Gestaltung?'
          ]}
          articleId={ARTICLE_ID}
          credits={[
            [
              'Quelle',
              '<a href="http://waldzustandsmonitor.de/" target="_blank">Waldzustandsmonitor  der TU München</a>'
            ],
            ['Text', 'Yannick Wiget'],
            ['Umsetzung', 'Andreas Moor']
          ]}
        />
      </div>
    </MDXProvider>
  )
}
